﻿import type { ApiProblem, UploadKind, UploadResponse } from "@ticketnjoy/types";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from "@ticketnjoy/types";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? "http://localhost:5258";

const LOCALE_COOKIE = "NEXT_LOCALE";

/**
 * Browser cookie'sinden (document.cookie) NEXT_LOCALE'i oku.
 * Server component'te çalışmaz — SSR için locale fetch'leri next-intl'in
 * getLocale() ile yapılmalı ve header opsiyonunda override edilmeli.
 */
function readLocaleFromCookie(): Locale {
  if (typeof document === "undefined") return DEFAULT_LOCALE;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${LOCALE_COOKIE}=([^;]+)`),
  );
  const value = match?.[1];
  return value && (SUPPORTED_LOCALES as readonly string[]).includes(value)
    ? (value as Locale)
    : DEFAULT_LOCALE;
}

/**
 * Backend ProblemDetails (RFC 7807) response'u ile eşleşen error sınıfı.
 * UI katmanında code'a göre mesaj gösterebilir.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public detail: string,
    public fieldErrors?: Record<string, string[]>,
  ) {
    super(detail || code);
    this.name = "ApiError";
  }
}

export interface ApiClientOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

/**
 * API istek helper'ı. Cookie'yi otomatik gönderir (credentials: include).
 * JSON body'yi serialize eder, response'u parse eder, hataları ApiError'a dönüştürür.
 */
export async function apiClient<T = unknown>(
  path: string,
  options: ApiClientOptions = {},
): Promise<T> {
  const { body, headers, ...rest } = options;

  // Backend middleware X-Locale header'ını ilk kaynak olarak kullanıyor;
  // cross-origin cookie'ler gelmeyebileceği için explicit gönderiyoruz.
  const locale = readLocaleFromCookie();

  const init: RequestInit = {
    credentials: "include",
    headers: {
      Accept: "application/json",
      "X-Locale": locale,
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    ...rest,
  };

  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, init);

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json") ||
                 contentType.includes("application/problem+json");

  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const problem = payload as ApiProblem;
    throw new ApiError(
      response.status,
      problem.title ?? "unknown_error",
      problem.detail ?? "Beklenmeyen bir hata oluştu.",
      problem.errors,
    );
  }

  return payload as T;
}

/**
 * Görsel upload — backend /organizer/uploads endpoint'ine multipart POST atar.
 * Ownership kontrolü server'da; client sadece kind + ilgili id'yi gönderir.
 *
 * JSON wrapper'ı (`apiClient`) kullanmıyoruz çünkü multipart'da Content-Type otomatik
 * set edilmeli (browser boundary'yi kendi ekler). Error shape aynı — ApiError fırlatılır.
 */
export async function uploadImage(params: {
  file: File;
  kind: UploadKind;
  eventId?: string;
  shopId?: string;
  signal?: AbortSignal;
  onProgress?: (pct: number) => void;
}): Promise<UploadResponse> {
  const { file, kind, eventId, shopId, signal, onProgress } = params;

  const formData = new FormData();
  formData.append("file", file);
  formData.append("kind", kind);
  if (eventId) formData.append("eventId", eventId);
  if (shopId) formData.append("shopId", shopId);

  const locale = readLocaleFromCookie();

  // Progress gerekiyorsa XHR, yoksa fetch. Fetch'in Response.body ReadableStream progress
  // Chrome'da upload tarafı için hâlâ sınırlı; XHR daha portable.
  if (onProgress) {
    return await new Promise<UploadResponse>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API_BASE_URL}/organizer/uploads`);
      xhr.withCredentials = true;
      xhr.setRequestHeader("Accept", "application/json");
      xhr.setRequestHeader("X-Locale", locale);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
      xhr.onload = () => {
        const contentType = xhr.getResponseHeader("content-type") ?? "";
        const isJson =
          contentType.includes("application/json") ||
          contentType.includes("application/problem+json");
        const payload = isJson && xhr.responseText
          ? JSON.parse(xhr.responseText)
          : xhr.responseText;
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(payload as UploadResponse);
        } else {
          const problem = payload as ApiProblem;
          reject(new ApiError(
            xhr.status,
            problem?.title ?? "upload_failed",
            problem?.detail ?? "Görsel yüklenemedi.",
            problem?.errors,
          ));
        }
      };
      xhr.onerror = () =>
        reject(new ApiError(0, "network_error", "Sunucuya ulaşılamadı."));
      xhr.onabort = () =>
        reject(new ApiError(0, "aborted", "Yükleme iptal edildi."));
      signal?.addEventListener("abort", () => xhr.abort());
      xhr.send(formData);
    });
  }

  const response = await fetch(`${API_BASE_URL}/organizer/uploads`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "X-Locale": locale },
    body: formData,
    signal,
  });

  const contentType = response.headers.get("content-type") ?? "";
  const isJson =
    contentType.includes("application/json") ||
    contentType.includes("application/problem+json");
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const problem = payload as ApiProblem;
    throw new ApiError(
      response.status,
      problem?.title ?? "upload_failed",
      problem?.detail ?? "Görsel yüklenemedi.",
      problem?.errors,
    );
  }

  return payload as UploadResponse;
}
